(() => {
  const COMPONENT_NAME = "accordion-disclosure";

  if (customElements.get(COMPONENT_NAME)) return;

  const ANIMATION_MS = 300;

  class AccordionDisclosure extends HTMLElement {
    #abortController = null;
    #rows = [];
    #reducedMotion = false;
    #timers = new WeakMap();
    #closingRows = new WeakSet();

    connectedCallback() {
      this.#abortController = new AbortController();
      this.#reducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;
      this.#rows = [...this.querySelectorAll("details.accordion-row")];

      for (const row of this.#rows) {
        this.#setupRow(row);
      }
    }

    disconnectedCallback() {
      for (const row of this.#rows) {
        this.#clearTimer(row);
        this.#closingRows.delete(row);
      }

      this.#abortController?.abort();
      this.#abortController = null;
      this.#rows = [];
    }

    #clearTimer(row) {
      const timerId = this.#timers.get(row);

      if (timerId != null) {
        window.clearTimeout(timerId);
        this.#timers.delete(row);
      }
    }

    #setupRow(row) {
      const summary = row.querySelector(".accordion-row__summary");
      const panel = row.querySelector(".accordion-row__panel");

      if (!summary || !panel) return;

      if (row.open) {
        panel.style.height = "auto";
      } else {
        panel.style.height = "0px";
      }

      summary.addEventListener(
        "click",
        (event) => {
          event.preventDefault();
          this.#toggleRow(row, panel);
        },
        { signal: this.#abortController.signal },
      );
    }

    #toggleRow(row, panel) {
      // `open` stays until the close animation ends so the panel remains visible;
      // treat an in-progress close as collapsed for toggle purposes.
      if (this.#closingRows.has(row)) {
        this.#openRow(row, panel);
        return;
      }

      if (row.hasAttribute("open")) {
        this.#closeRow(row, panel);
        return;
      }

      this.#openRow(row, panel);
    }

    #openRow(row, panel) {
      this.#clearTimer(row);
      this.#closingRows.delete(row);
      row.setAttribute("open", "");

      if (this.#reducedMotion) {
        panel.style.height = "auto";
        return;
      }

      // Animate from the current height so reopening mid-close does not jump.
      panel.style.height = `${panel.getBoundingClientRect().height}px`;

      requestAnimationFrame(() => {
        panel.style.height = `${panel.scrollHeight}px`;
      });

      const timerId = window.setTimeout(() => {
        if (row.hasAttribute("open") && !this.#closingRows.has(row)) {
          panel.style.height = "auto";
        }

        this.#timers.delete(row);
      }, ANIMATION_MS);

      this.#timers.set(row, timerId);
    }

    #closeRow(row, panel) {
      this.#clearTimer(row);
      this.#closingRows.add(row);

      if (this.#reducedMotion) {
        panel.style.height = "0px";
        row.removeAttribute("open");
        this.#closingRows.delete(row);
        return;
      }

      panel.style.height = `${panel.scrollHeight}px`;

      requestAnimationFrame(() => {
        panel.style.height = "0px";
      });

      const timerId = window.setTimeout(() => {
        row.removeAttribute("open");
        this.#closingRows.delete(row);
        this.#timers.delete(row);
      }, ANIMATION_MS);

      this.#timers.set(row, timerId);
    }
  }

  customElements.define(COMPONENT_NAME, AccordionDisclosure);
})();
